"use client";

import { TopBar, TopNavBar, Footer } from '@/layout';
import { Fragment } from 'react';
import JourneyHero from './components/JourneyHero';
import Hero from './components/Hero';
import Statistics from './components/Statistics';
import TeamMembers from './components/TeamMembers';
import Timeline from './components/Timeline';
import Partnerships from './components/Partnerships';
import Founder from './components/Founder';
import CompanyStory from './components/CompanyStory';
import AboutHeroIconBoxesStrip from './components/AboutHeroIconBoxesStrip';
import PartnerSection from '../home/components/PartnerSection';
import ContactPartnerForm from '../contact/components/ContactPartnerForm';
import { useAboutPage } from '@/hooks/useAboutPage';
import { Spinner } from 'react-bootstrap';

// Component mapping: maps section keys to their React components
const sectionComponentMap = {
  'journey_hero': JourneyHero,
  'hero': Hero,
  'team_members': TeamMembers,
  'partnerships': Partnerships,
  'statistics': Statistics,
  'timeline': Timeline,
  'founder': Founder,
  'company_story': CompanyStory,
  'hero_icon_boxes': AboutHeroIconBoxesStrip,
  'partner': PartnerSection,
  'contact_form_partners': ContactPartnerForm,
};

const About = ({ initialSections = null }) => {
  const { sections, loading, error } = useAboutPage(null, initialSections);

  if (loading) {
    return (
      <>
        <TopBar />
        <TopNavBar />
        <main>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar />
        <TopNavBar />
        <main>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <p className="text-danger">Error loading about page: {error}</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <TopNavBar />
      <main>
        {(() => {
          const activeSections = sections
          .filter(section => section.is_active)
          .sort((a, b) => {
            // Sort by display_order, then by id as fallback
            const orderA = a.display_order ?? 999;
            const orderB = b.display_order ?? 999;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return (a.id ?? 0) - (b.id ?? 0);
          });

          const iconBoxesSection = activeSections.find((section) => section.section_key === 'hero_icon_boxes');
          let hasRenderedIconBoxes = false;

          return activeSections
          .filter((section) => section.section_key !== 'hero_icon_boxes')
          .map((section) => {
            const Component = sectionComponentMap[section.section_key];
            if (!Component) {
              console.warn(`No component mapped for section key: ${section.section_key}`);
              return null;
            }
            if (section.section_key === 'hero') {
              hasRenderedIconBoxes = true;
              return (
                <Fragment key={section.id || section.section_key}>
                  <Component sectionData={section} />
                  <AboutHeroIconBoxesStrip sectionData={iconBoxesSection} />
                </Fragment>
              );
            }

            if (section.section_key === 'partnerships' && !hasRenderedIconBoxes) {
              hasRenderedIconBoxes = true;
              return (
                <Fragment key={section.id || section.section_key}>
                  <AboutHeroIconBoxesStrip sectionData={iconBoxesSection} />
                  <Component sectionData={section} />
                </Fragment>
              );
            }

            return <Component key={section.id || section.section_key} sectionData={section} />;
          });
        })()}
      </main>
      <Footer />
    </>
  );
};

export default About;
